import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { DocumentDetailReconstructionResult } from '@avisoma/shared';
import backend from '../../services/backend';
import {
  getIsReconstructionVisualLoading,
  getReconstructedDocumentsPaginationState,
  isReconstructionGenerationConflict,
  RECONSTRUCTED_DOCUMENTS_PAGE_SIZE,
} from './reconstructedDocumentsPagination';

type UseDocumentReconstructedDocumentsOptions = {
  documentId: string;
  reconstructionStatus: 'done' | 'error' | 'processing' | 'pages-done' | undefined;
  reconstructionCompletedAt: string | undefined;
  reconstruction: DocumentDetailReconstructionResult | undefined;
  isPageWorkloadKnown: boolean;
  isHugeClassificationVisual: boolean;
  hasSegmentedLineage: boolean;
  pageCount: number;
};

/**
 * The document view's single reconstruction cursor controller. Its first page is always eager
 * for the preview; only visual consumers ask it to drain the remaining cursor pages.
 */
export function useDocumentReconstructedDocuments({
  documentId,
  reconstructionStatus,
  reconstructionCompletedAt,
  reconstruction,
  isPageWorkloadKnown,
  isHugeClassificationVisual,
  hasSegmentedLineage,
  pageCount,
}: UseDocumentReconstructedDocumentsOptions) {
  const queryClient = useQueryClient();
  const [visualsRequested, setVisualsRequested] = useState(false);
  const [recoveryVersion, setRecoveryVersion] = useState(0);
  const fetchingNextPageRef = useRef(false);
  const reconstructedCount = reconstruction?.reconstructedCount ?? 0;
  const enabled = reconstructionStatus === 'done';
  const generation = `${reconstructionStatus ?? 'unavailable'}:${reconstructionCompletedAt ?? 'unavailable'}`;
  const queryKey = useMemo(
    () => ['document-reconstructed-documents', documentId, generation, recoveryVersion],
    [documentId, generation, recoveryVersion],
  );

  const query = useInfiniteQuery({
    queryKey,
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      backend
        .listDocumentReconstructedDocuments(documentId, {
          limit: String(RECONSTRUCTED_DOCUMENTS_PAGE_SIZE),
          ...(pageParam ? { cursor: pageParam } : {}),
        })
        .then((response) => response.data),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined),
    retry: (failureCount, error) => !isReconstructionGenerationConflict(error) && failureCount < 2,
    staleTime: 30_000,
  });

  const pagination = useMemo(
    () =>
      getReconstructedDocumentsPaginationState({
        pages: query.data?.pages ?? [],
        fallbackTotalCount: reconstructedCount,
        isPageWorkloadKnown,
        isHugeClassificationVisual,
        hasSegmentedLineage,
        pageCount,
        visualsRequested,
        isFetchingNextPage: query.isFetchingNextPage,
        hasError: query.isError,
      }),
    [
      hasSegmentedLineage,
      isHugeClassificationVisual,
      isPageWorkloadKnown,
      pageCount,
      query.data?.pages,
      query.isError,
      query.isFetchingNextPage,
      reconstructedCount,
      visualsRequested,
    ],
  );

  // Changing the cache key discards every old cursor page before starting at cursor=null again.
  // This is essential after the API returns 409 for a reconstruction generation/source change.
  const restart = useCallback(() => {
    queryClient.removeQueries({ queryKey });
    setRecoveryVersion((version) => version + 1);
  }, [queryClient, queryKey]);

  const fetchNextPage = useCallback(async () => {
    if (!pagination.hasMore || query.isFetchingNextPage || fetchingNextPageRef.current) return;
    fetchingNextPageRef.current = true;
    try {
      await query.fetchNextPage({ throwOnError: true });
    } catch (error) {
      if (isReconstructionGenerationConflict(error)) {
        restart();
      }
    } finally {
      fetchingNextPageRef.current = false;
    }
  }, [pagination.hasMore, query, restart]);

  // A non-huge document needs complete geometry without an extra click. After a huge-document
  // gate is opened, the same effect drains the shared cache before that visual can mount.
  useEffect(() => {
    if (pagination.nextAction !== 'drain') return;
    // Defer the Query state change to the timer callback rather than issuing it during the
    // effect's synchronous phase. This also gives React a paint between cursor pages.
    const timer = window.setTimeout(() => {
      void fetchNextPage();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [fetchNextPage, pagination.nextAction]);

  const requestVisuals = useCallback(() => {
    setVisualsRequested(true);
  }, []);

  const isVisualLoading = getIsReconstructionVisualLoading({
    isQueryEnabled: enabled,
    shouldDrainForVisuals: pagination.shouldDrainForVisuals,
    isQueryPending: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    hasMore: pagination.hasMore,
    hasError: query.isError,
  });

  return {
    ...pagination,
    isLoading: enabled && query.isPending,
    isLoadingMore: query.isFetchingNextPage,
    error: query.error instanceof Error ? query.error : undefined,
    isGenerationConflict: isReconstructionGenerationConflict(query.error),
    isVisualLoading,
    recoveryVersion,
    requestVisuals,
    loadMore: fetchNextPage,
    retry: restart,
  };
}
